import React, { Fragment, useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader } from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import {
    Briefcase,
    CheckCircle,
    Users,
    FileText,
    Package,
    Clock,
} from "react-feather";

/** Map stat title to react-feather icon so icons always display (no dependency on Font Awesome). */
const STAT_ICON_MAP = {
    "Total Jobs": Briefcase,
    "Active Jobs": CheckCircle,
    "Recruiters": Package,
    "Jobseekers": Users,
    "Applications": FileText,
    "Total Companies": Package,
    "Pending Companies": Clock,
};

const AdminDashboard = () => {
    const [stats, setStats] = useState([]);
    const [recentJobs, setRecentJobs] = useState([]);
    const [activitySummary, setActivitySummary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setError(null);
                const response = await AxiosInstance().post("/admin/dashboard_stats", {});
                if (response.data.status && response.data.data) {
                    const d = response.data.data;
                    setStats(d.stats || []);
                    setRecentJobs(d.recentJobs || []);
                    setActivitySummary(d.activitySummary || []);
                } else {
                    setError(response.data.message || "Failed to load dashboard data.");
                }
            } catch (err) {
                console.error("Error fetching admin stats:", err);
                setError(err.response?.data?.message || "Failed to load dashboard. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const getStatusBadgeClass = (status) => {
        if (status === "Active") return "success";
        if (status === "Pending") return "warning";
        return "secondary";
    };

    if (loading) {
        return (
            <Fragment>
                <Breadcrumbs title="Admin Dashboard" mainTitle="Dashboard Overview" />
                <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: "60vh" }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mb-0">Loading dashboard...</p>
                    </div>
                </div>
            </Fragment>
        );
    }

    if (error) {
        return (
            <Fragment>
                <Breadcrumbs title="Admin Dashboard" mainTitle="Dashboard Overview" />
                <Container fluid className="p-4">
                    <Card className="border-0 shadow-sm">
                        <CardBody className="text-center py-5">
                            <i className="fa fa-exclamation-circle fa-3x text-warning mb-3" />
                            <h5 className="text-danger mb-2">Unable to load dashboard</h5>
                            <p className="text-muted mb-0">{error}</p>
                        </CardBody>
                    </Card>
                </Container>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Admin Dashboard" mainTitle="Dashboard Overview" />
            <Container fluid={true} className="p-4">
                {/* Stat cards */}
                <Row className="g-3 mb-4">
                    {stats.map((stat, index) => {
                        const IconComponent = STAT_ICON_MAP[stat.title] || Briefcase;
                        return (
                            <Col xl="3" sm="6" key={index}>
                                <Card className="border-0 shadow-sm h-100 dashboard-stat-card">
                                    <CardBody className="d-flex align-items-center py-4">
                                        <div className={`p-3 rounded-circle bg-light me-3 d-flex align-items-center justify-content-center`}>
                                            <IconComponent size={32} className={`text-${stat.color}`} />
                                        </div>
                                        <div className="flex-grow-1">
                                            <p className="mb-0 text-muted small text-uppercase fw-medium">{stat.title}</p>
                                            <h4 className="mb-0 fw-bold mt-1">{stat.count.toLocaleString()}</h4>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                <Row>
                    <Col xl="8">
                        <Card className="border-0 shadow-sm mb-4">
                            <CardHeader className="bg-white border-bottom py-3 px-4">
                                <h5 className="mb-0 fw-bold">Recent Job Postings</h5>
                                <small className="text-muted">Latest jobs from the database</small>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="px-4 py-3 fw-semibold">Job Title</th>
                                                <th className="fw-semibold">Company</th>
                                                <th className="fw-semibold">Posted Date</th>
                                                <th className="fw-semibold">Status</th>
                                                <th className="text-end px-4 fw-semibold">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentJobs.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5 text-muted">
                                                        <i className="fa fa-inbox fa-2x mb-2 d-block opacity-50" />
                                                        No job postings yet.
                                                    </td>
                                                </tr>
                                            ) : (
                                                recentJobs.map((job) => (
                                                    <tr key={job.id}>
                                                        <td className="px-4 py-3 fw-medium">{job.title}</td>
                                                        <td>{job.company}</td>
                                                        <td>{job.date}</td>
                                                        <td>
                                                            <span className={`badge bg-${getStatusBadgeClass(job.status)} px-2 py-1 rounded`}>
                                                                {job.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-end px-4">
                                                            <Link to={`/job/admin/manage-jobs?job=${job.id}`} className="btn btn-sm btn-outline-primary">
                                                                View
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl="4">
                        <Card className="border-0 shadow-sm h-100">
                            <CardHeader className="bg-white border-bottom py-3">
                                <h5 className="mb-0 fw-bold">Activity Summary</h5>
                                <small className="text-muted">From database</small>
                            </CardHeader>
                            <CardBody>
                                {activitySummary.length === 0 ? (
                                    <p className="text-muted small mb-0 text-center py-3">No activity data yet.</p>
                                ) : (
                                    <>
                                        {activitySummary.map((item, idx) => (
                                            <div key={idx} className="mb-4">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted small">{item.label}</span>
                                                    <span className="fw-semibold small">{item.value}</span>
                                                </div>
                                                <div className="progress" style={{ height: "8px" }}>
                                                    <div
                                                        className={`progress-bar bg-${item.color}`}
                                                        role="progressbar"
                                                        style={{ width: `${Math.min(100, item.percent)}%` }}
                                                        aria-valuenow={item.percent}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <hr />
                                        <div className="mt-3 text-center">
                                            <p className="text-muted small mb-0">Data from database</p>
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <style>{`
                .dashboard-stat-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
                .dashboard-stat-card:hover { transform: translateY(-2px); box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.1) !important; }
                .table-hover tbody tr:hover { background-color: rgba(0,0,0,0.02); }
            `}</style>
        </Fragment>
    );
};

export default AdminDashboard;
